import React from 'react';
import SimpleDialog from '../SimpleDialog/SimpleDialog';
import { SaveDialogProps } from './SaveDialog.types';
import './SaveDialog.scss';
import OptionsView from './components/OptionsView';
import FormView from './components/FormView';
import SuccessView from './components/SuccessView';
import FailureView from './components/FailureView';
import { useSaveDialog } from './hooks/useSaveDialog';

const SaveDialog: React.FC<SaveDialogProps> = ({
  isOpen,
  onClose,
  width,
  height,
  isDarkMode = true,
  source = '',
}) => {
  const {
    step,
    selectedOption,
    selectedPrefix,
    setSelectedPrefix,
    isMarketingAccepted,
    setIsMarketingAccepted,
    backendError,
    setBackendError,
    setFieldValue,
    loading,
    isSent,
    isFailed,
    variantID,
    variantName,
    dealers,
    models,
    errors,
    isFormValid,
    handleOptionClick,
    handleDialogClose,
    handleSendConfig,
    validateSingleField,
    clearError,
    t,
    firstName,
    lastName,
    email,
    phone,
    selectedModel,
    selectedDealer,
    testDriveDate,
    testDriveTime,
    comments,
    qrCodeUrl,
    downloadQRCode,
    openQRCodeInNewTab,
  } = useSaveDialog(isOpen, onClose, source);

  const title =
    step === 'options'
      ? t(
          'staticTranslations.saveDialog.optionsTitle',
          'How would you like to proceed?'
        )
      : selectedOption === 'whatsapp'
      ? t('staticTranslations.saveDialog.optionWhatsapp', 'Save to WhatsApp')
      : selectedOption === 'email'
      ? t('staticTranslations.saveDialog.optionEmail', 'Save to Email')
      : selectedOption === 'test-drive'
      ? t('staticTranslations.saveDialog.optionTestDrive', 'Book a Test Drive')
      : t(
          'staticTranslations.saveDialog.optionCallback',
          'Request a call Back'
        );

  const computedWidth = step === 'options' ? 880 : width ?? 720;

  // Only the booking forms get a confirmation screen. Save to WhatsApp / Save
  // to Email stay on the form and flip the CTA to "Sent!", matching 1.0 —
  // SaveNShare has no success view, and discards the QR the same call returns.
  const isBookingForm =
    selectedOption === 'test-drive' || selectedOption === 'callback';

  const renderContent = () => {
    if (isSent && isBookingForm) {
      return (
        <SuccessView
          onClose={onClose}
          firstName={firstName}
          lastName={lastName}
          email={email}
          phone={phone}
          selectedModel={selectedModel}
          variantName={variantName || variantID || ''}
          selectedPrefix={selectedPrefix}
          qrCodeUrl={qrCodeUrl}
          onDownloadQR={downloadQRCode}
          onOpenQR={openQRCodeInNewTab}
          t={t}
        />
      );
    }
    if (isFailed) {
      return <FailureView onRetry={handleSendConfig} t={t} />;
    }
    if (step === 'options') {
      return <OptionsView onOptionClick={handleOptionClick} t={t} />;
    }
    return (
      <FormView
        selectedOption={selectedOption}
        selectedPrefix={selectedPrefix}
        setSelectedPrefix={setSelectedPrefix}
        isMarketingAccepted={isMarketingAccepted}
        setIsMarketingAccepted={setIsMarketingAccepted}
        backendError={backendError}
        setBackendError={setBackendError}
        setFieldValue={setFieldValue}
        loading={loading}
        isSent={isSent}
        isFormValid={isFormValid}
        dealers={dealers}
        models={models}
        errors={errors}
        handleSendConfig={handleSendConfig}
        validateSingleField={validateSingleField}
        clearError={clearError}
        t={t}
        firstName={firstName}
        lastName={lastName}
        email={email}
        phone={phone}
        selectedModel={selectedModel}
        selectedDealer={selectedDealer}
        testDriveDate={testDriveDate}
        testDriveTime={testDriveTime}
        comments={comments}
      />
    );
  };

  return (
    <SimpleDialog
      isOpen={isOpen}
      onClose={handleDialogClose}
      title={title}
      className={`save-dialog${isDarkMode ? '' : ' save-dialog--light'}${step === 'options' ? ' save-dialog--options-step' : ''}`}
      width={computedWidth}
      height={height}
      backdropDarkness="dark"
    >
      {renderContent()}
    </SimpleDialog>
  );
};

export default SaveDialog;
